import { useEffect, useState } from 'react'

/**
 * useMediaQuery — subscribe to a CSS media query and re-render on change.
 *
 * SSR/first-paint safe: returns `false` until mounted, then the real value.
 * Used by the responsive design kit (DataView table⇄card collapse, the laptop
 * sidebar tier, etc.).
 */
export function useMediaQuery(query: string): boolean {
  const getMatch = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false

  const [matches, setMatches] = useState(getMatch)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    onChange()
    // Safari <14 only supports addListener/removeListener.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }
    mql.addListener(onChange)
    return () => mql.removeListener(onChange)
  }, [query])

  return matches
}

/** True below Tailwind `md` (< 768px) — where wide tables must reflow to cards. */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)')
}

/** True in the laptop band `lg`..`xl-1` (1024–1279px). */
export function useIsLaptop(): boolean {
  return useMediaQuery('(min-width: 1024px) and (max-width: 1279px)')
}

export default useMediaQuery
